import { Moment } from 'moment';
import { sum } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { FinanceProvider } from '@upstart/harmony/lib/constants';
import QuickStats from '../../components/ResultsDashboard/QuickStats';
import LoadingOverlay from '../../components/LoadingOverlay';
import { useDateRange } from '../../util/useDateRange';

type Metric = {
  [index: string]: number;
};

interface Metrics {
  dealerMetrics: ({ dealerName: string } & Metric)[];
  providerMetrics: Record<string, Record<string, number>>;
}

interface Props {
  getMetrics: (params: {
    startDate: string;
    endDate: string;
  }) => Promise<Metrics>;
  columns: string[][];
}

function ClientLendingResults(props: Props) {
  const [startDate, endDate, picker, rangeLabel, fullRangeLabel] =
    useDateRange();
  const [selectedDealers, setSelectedDealers] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const dealers = metrics?.dealerMetrics ?? [];
  const providers = metrics?.providerMetrics ?? {};
  const westlake = providers?.[FinanceProvider.WESTLAKE] ?? {};
  const totalSeen = sum(dealers.map((d) => d.lendingApprovalSeen || 0));
  const totalSelected = sum(dealers.map((d) => d.lendingApprovalSelected || 0));
  const { getMetrics } = props;

  const startStr = (startDate as Moment).toISOString();
  const endStr = (endDate as Moment).toISOString();

  useEffect(() => {
    const fetchMetrics = async () => {
      const data = await getMetrics({ startDate: startStr, endDate: endStr });
      setMetrics(data);
    };
    fetchMetrics();
  }, [startStr, endStr, getMetrics]);

  return (
    <LoadingOverlay active={!metrics}>
      <h4 className="pt-15">
        {rangeLabel} {picker}
        <span className="pt-5 pl-5">{fullRangeLabel}</span>
      </h4>
      <div id="results">
        <QuickStats
          stats={[
            {
              label: 'Total Approvals Displayed',
              value: totalSeen,
            },
            {
              label: 'Total Westlake Approvals Displayed',
              value: westlake.lendingApprovalSeen || 0,
            },
            {
              label: 'Total Deals Selected',
              value: totalSelected,
            },
            {
              label: 'Total Westlake Approvals Selected',
              value: westlake.lendingApprovalSelected || 0,
            },
          ]}
        />
      </div>
      <Typeahead
        multiple
        className="w-50 pt-15 pb-15"
        id="dealer-filter"
        placeholder="Filter by dealer"
        selected={selectedDealers}
        onChange={setSelectedDealers}
        options={dealers.map((m) => m.dealerName)}
      />
      <BootstrapTable
        data={dealers.filter(
          (d) =>
            !selectedDealers.length || selectedDealers.includes(d.dealerName),
        )}
        pagination
        striped
      >
        <TableHeaderColumn isKey dataField="dealerName">
          Dealership Name
        </TableHeaderColumn>
        {props.columns.map((c) => (
          <TableHeaderColumn key={c[0]} dataField={c[0]}>
            {c[1]}
          </TableHeaderColumn>
        ))}
      </BootstrapTable>
    </LoadingOverlay>
  );
}

export default ClientLendingResults;
